import { useCallback, useEffect, useRef, useState } from "react";

// Generic resource loader for lazy adapters and controller reads. Keys include
// account/team/resource scope; abort plus generation checks reject late results.
export function usePageResource(load, { identity, enabled = true } = {}) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(null);
  const request = useRef(0);
  const retry = useCallback(() => {
    request.current += 1;
    setState((current) => current && { ...current, status: "loading", error: null });
    setAttempt((value) => value + 1);
  }, []);
  useEffect(() => {
    if (!enabled) return;
    const generation = ++request.current;
    const controller = new AbortController();
    setState((current) => ({
      identity, load, status: "loading", error: null,
      data: current?.identity === identity && current.load === load ? current.data : undefined,
    }));
    Promise.resolve().then(() => {
      if (controller.signal.aborted) return undefined;
      return load({ signal: controller.signal });
    }).then((data) => {
      if (controller.signal.aborted || generation !== request.current) return;
      setState({ identity, load, status: "ready", data, error: null });
    }, (error) => {
      if (controller.signal.aborted || generation !== request.current) return;
      setState((current) => ({
        identity, load, status: "error", error,
        data: current?.identity === identity && current.load === load ? current.data : undefined,
      }));
    });
    return () => { controller.abort(); request.current += 1; };
  }, [identity, enabled, load, attempt]);
  const current = enabled && state?.identity === identity && state.load === load
    ? state : { status: "loading", data: undefined, error: null };
  return { ...current, retry };
}
